import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnInit, inject, signal } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonPrimaryComponent } from 'projects/components/button-primary/button-primary.component';
import { ErrorBadgeComponent } from 'projects/components/error-badge/error-badge.component';
import { InputComponent } from 'projects/components/input/input.component';
import { IconComponent } from 'projects/components/pay-icon/icon.component';
import { IconNames } from 'projects/components/pay-icon/IconNames';
import { PayErrorResponse } from 'projects/login/src/app/services/auth/models/pay-error-response';
import { environment } from 'projects/login/src/environments/environment';
import { AuthService } from '../../../services/auth/auth.service';
import { TokenService } from '../../../services/auth/token.service';
import { TwoFaService } from '../../../services/auth/two-fa.service';
import { PagePopulationService } from '../../../services/page-population/page-population.service';
import { RedirectService } from '../../../services/redirect/redirect.service';
import { LastUsedEmails } from './last-used-emails/last-used-emails';
import { map } from 'rxjs';
import { LastUsedDevice } from '../../../services/auth/models/DeviceAccountsResponse';
import { CheckboxComponent } from '../../../../../../components/checkbox/checkbox.component';

@Component({
  selector: 'pay-login-login-page',
  imports: [
    FormsModule,
    InputComponent,
    ReactiveFormsModule,
    ButtonPrimaryComponent,
    TranslateModule,
    ErrorBadgeComponent,
    IconComponent,
    LastUsedEmails,
    CheckboxComponent,
  ],
  templateUrl: './login-page.component.html',
})
export class LoginPageComponent implements OnInit {
  private readonly _cdRef = inject(ChangeDetectorRef);
  private readonly _authLoginService = inject(AuthService);
  private readonly _router = inject(Router);
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _redirectService = inject(RedirectService);
  private readonly _tokenService = inject(TokenService);
  private readonly _twoFaService = inject(TwoFaService);
  private readonly _pagePopulationService = inject(PagePopulationService);
  private readonly _translateService = inject(TranslateService);

  protected readonly emailSetWithPicker = signal(false);
  protected readonly lastUsedEmails = signal<LastUsedDevice[]>([]);
  isSubmitting = signal(false);
  error?: PayErrorResponse;
  referrerpage?: string;

  form = new FormGroup({
    username: new FormControl<string>('', [Validators.required, Validators.email]),
    password: new FormControl<string>('', [Validators.required]),
  });
  rememberEmail = new FormControl<boolean>(false);

  constructor() {
    const _translateService = this._translateService;

    const navigation = this._router.currentNavigation();
    if (navigation?.extras.state) {
      this.error = navigation.extras.state['givenError'];
    }
    this._activatedRoute.queryParams.subscribe((params) => {
      this.referrerpage = params['referrer'];
    });
    if (!TokenService.sessionId && !document.location.pathname) {
      this._redirectService.goToInvalidSession();
    }
    _translateService.onLangChange.subscribe({
      next: () => {
        this.error = undefined;
      },
    });

    this._authLoginService
      .getDeviceAccounts()
      .pipe(map((x) => x.accounts))
      .subscribe({
        next: (account: LastUsedDevice[]) => {
          this.lastUsedEmails.set(
            account.sort((a, b) => {
              return new Date(b.lastUsedAt).getTime() - new Date(a.lastUsedAt).getTime();
            }),
          );
        },
      });
  }

  ngOnInit() {
    this._activatedRoute.queryParams.subscribe((params) => {
      const redirectUrl = params['redirectUrl'];
      if (redirectUrl) {
        this._redirectService.setRedirectUrl(redirectUrl);
      } else {
        this._redirectService.removeRedirectUrl();
      }
    });
    this._pagePopulationService.populationInformation = null;
  }

  submit() {
    if (this.isSubmitting() || this.form.invalid) {
      return;
    }

    this.isSubmitting.set(true);
    this._cdRef.detectChanges();

    this.processLogin(TokenService.sessionId!);
  }

  goToResetPassword() {
    this._router.navigateByUrl('/reset-password').then();
  }

  redirectToSignup() {
    document.location.href = environment.signupUrl;
  }

  processLogin(sessionId: string) {
    this._tokenService.accessToken = undefined;
    this._authLoginService
      .token(sessionId, {
        username: this.form.controls.username.value!,
        password: this.form.controls.password.value!,
        rememberEmail: this.rememberEmail.value!,
      })
      .subscribe({
        next: (r) => {
          const response = r;
          if (this._authLoginService.isMFAChallengedResponse(response)) {
            this._tokenService.accessToken = response.authenticationToken.token;

            if (response.setupRequired) {
              this._twoFaService.resetQrData();
              this._router.navigateByUrl('/authentication-setup').then();
            } else {
              this._pagePopulationService.populationInformation = {
                method: response.challengedMfaMethod,
                contactInfo: this.form.controls.username.value!,
                bearer: response.authenticationToken.token,
              };
              this._router.navigateByUrl('/two-factor-authentication').then();
            }
          } else {
            this._redirectService.goRedirectUrl(response.redirectUrl);
          }
        },
        error: (response: HttpErrorResponse) => {
          this.error = response.error as PayErrorResponse;

          // If the error is any of these, your session is cooked
          if (['PAY-6009', 'PAY-6010', 'PAY-6011', 'PAY-6014', 'PAY-6015', 'PAY-6025'].includes(this.error.code) || response.status === 410) {
            this._router.navigate(['/session-error'], { state: { email: this.form.controls.username.value! } }).then();
            return;
          }
          this.isSubmitting.set(false);

          this._cdRef.detectChanges();
        },
      });
  }

  protected readonly IconNames = IconNames;

  protected emailClickHandler(e: { uuid: string; email: string; lastUsedAt: Date }) {
    this.form.controls.username.setValue(e.email);
    this.emailSetWithPicker.set(true);
  }

  protected clearEmailSetWithPicker() {
    this.emailSetWithPicker.set(false);
    this.form.controls.username.reset();
  }

  protected handleEmailDelete(e: string) {
    this.lastUsedEmails.update((x) => {
      return x.filter((y) => y.uuid !== e);
    });
    this._authLoginService.deleteDeviceAccount(e).subscribe();
  }
}
